/**
 * Chart capture utilities for PNG export.
 * Uses html-to-image to capture offscreen-rendered chart.
 */

import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { createElement } from "react";
import type { ComponentProps } from "react";
import { toCanvas } from "html-to-image";
import type { ExportLayoutInput, ExportOptions } from "./types";
import { EXPORT_MAX_SAFE_WIDTH } from "./types";
import { ExportRenderer } from "@/components/Export/ExportRenderer";
import { calculateExportDimensions } from "./exportLayout";
import { REACT_RENDER_WAIT_MS, SVG_BACKGROUND_WHITE } from "./constants";
import {
  waitForFonts,
  waitForPaint,
  createOffscreenContainer,
  removeOffscreenContainer,
} from "./helpers";

/**
 * Floor for device pixel ratio used for canvas capture.
 * Ensures Retina-quality (2×) output on 1× DPI displays where
 * `window.devicePixelRatio` would otherwise be 1. Also used as the lower
 * bound in the upper-cap clamp (see `computeMaxCapturePixelRatio`) so that
 * the capped ratio never falls below 2× even when EXPORT_MAX_SAFE_WIDTH and
 * screen width would otherwise produce a max ratio less than 2.
 */
const MIN_PIXEL_RATIO = 2;

/**
 * Compute the maximum safe pixel ratio for canvas capture.
 * Prevents oversized canvases on high-DPR displays — e.g. a 4K display with
 * dpr=4 would otherwise produce a canvas 4× wider than the layout width,
 * potentially exceeding the browser's canvas size limit (EXPORT_MAX_SAFE_WIDTH).
 *
 * Computed inside `captureContainerToCanvas` rather than at module load time so
 * that importing this module in non-browser environments (tests, SSR) is safe —
 * `window.screen` is only accessed when an actual capture is taking place.
 */
function computeMaxCapturePixelRatio(): number {
  const screenWidth =
    typeof window !== "undefined" ? (window.screen?.width ?? 1920) : 1920;
  return Math.floor(EXPORT_MAX_SAFE_WIDTH / Math.max(screenWidth, 1));
}

/**
 * Maximum time in milliseconds to wait for `toCanvas` to complete.
 * Guards against an indefinite hang when the browser tab is backgrounded
 * during export (requestAnimationFrame is throttled or paused in hidden tabs).
 */
export const CANVAS_CAPTURE_TIMEOUT_MS = 30_000;

/**
 * Error message thrown when `toCanvas` does not complete within
 * `CANVAS_CAPTURE_TIMEOUT_MS`. Defined as a constant so that the message
 * and the timeout duration stay in sync and can be referenced in tests.
 */
export const CANVAS_CAPTURE_TIMEOUT_MESSAGE =
  `Canvas capture timed out after ${CANVAS_CAPTURE_TIMEOUT_MS / 1000}s. ` +
  "Try keeping the tab in the foreground during export.";

export interface CaptureChartParams extends ExportLayoutInput {
  /** Project name for export filename */
  projectName?: string;
}

/**
 * Create a React root inside `container`, render ExportRenderer into it, and
 * wait for React's initial paint to settle before proceeding.
 *
 * The root is created synchronously so it is always available for the caller
 * to unmount in a finally block, even if the settle timeout is interrupted.
 *
 * **Resource ownership**: the caller MUST call `root.unmount()` after use to
 * prevent a React root from remaining attached to the DOM indefinitely.
 * Failure to unmount after capture produces a memory leak and leaves the
 * offscreen container's React tree alive after `removeOffscreenContainer`.
 *
 * @param container - The DOM container to render into
 * @param props - Props forwarded to ExportRenderer
 * @returns The React root — caller MUST call `root.unmount()` when done
 */
async function renderAndSettle(
  container: HTMLDivElement,
  props: ComponentProps<typeof ExportRenderer>
): Promise<Root> {
  // createRoot is synchronous — root is always set before any async work.
  const root = createRoot(container);
  root.render(createElement(ExportRenderer, props));
  // Yield one macro-task (REACT_RENDER_WAIT_MS) so React's async scheduler
  // can flush its first commit before waitForFonts/waitForPaint take over.
  // See REACT_RENDER_WAIT_MS in constants.ts for the rationale.
  //
  // LIMITATION: root.render() only propagates synchronous throws. Errors
  // thrown inside ExportRenderer during React's asynchronous commit phase are
  // handled by React's error boundary mechanism. If ExportRenderer has no
  // error boundary, React logs the error and silently unmounts the tree,
  // resulting in an empty canvas without rejecting this promise. The
  // zero-dimension canvas guard in captureContainerToCanvas() catches this
  // case by throwing when html-to-image captures a 0×0 canvas.
  await new Promise<void>((resolve) =>
    setTimeout(resolve, REACT_RENDER_WAIT_MS)
  );
  return root;
}

/**
 * Race a promise against a timeout, cancelling the timer on completion.
 *
 * Guards against indefinite hangs when the browser tab is backgrounded and
 * `requestAnimationFrame` is throttled or paused (e.g. during html-to-image
 * capture). The timer is cancelled as soon as `promise` settles so no live
 * timers leak in tests or rapid export sequences.
 *
 * @param promise - The operation to race against the timeout
 * @param timeoutMs - Maximum time to wait in milliseconds
 * @param timeoutMessage - Error message thrown when the timeout fires
 * @returns Resolves with the value from `promise`, or rejects with a timeout error
 */
export async function raceWithTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Make the container visible, run `toCanvas`, and race it against a timeout.
 *
 * Separated from `captureChart` to keep each function under 50 lines.
 * The container is made visible immediately before capture so that
 * html-to-image can measure layout; it is hidden again by the caller's
 * finally block via `removeOffscreenContainer`.
 *
 * @param container - The offscreen container holding the rendered chart
 * @param dimensions - Expected canvas width and height in pixels
 * @param background - Export background setting used to derive backgroundColor
 * @returns A canvas element containing the captured chart
 * @throws If canvas capture times out (e.g. tab backgrounded during export)
 * @throws If the captured canvas has zero dimensions (silent render failure)
 */
async function captureContainerToCanvas(
  container: HTMLDivElement,
  dimensions: { width: number; height: number },
  background: ExportOptions["background"]
): Promise<HTMLCanvasElement> {
  // Make visible for capture (html-to-image needs visible elements).
  // The container is briefly visible here (opacity 1) but is removed by
  // removeOffscreenContainer() in the caller's finally block as soon as
  // capture completes or throws, bounding the visible window to capture duration.
  container.style.opacity = "1";
  await waitForPaint();

  // Cap pixel ratio to prevent canvases wider than EXPORT_MAX_SAFE_WIDTH on
  // high-DPR displays (e.g. 4K at dpr=4 would produce a canvas 4× the layout
  // width, potentially hitting the browser's canvas size limit). MIN_PIXEL_RATIO
  // is applied as a lower bound in both clamps so the ratio never drops below 2×.
  const maxCapturePixelRatio = computeMaxCapturePixelRatio();
  const pixelRatio = Math.min(
    Math.max(window.devicePixelRatio, MIN_PIXEL_RATIO),
    Math.max(maxCapturePixelRatio, MIN_PIXEL_RATIO)
  );

  const capturePromise = toCanvas(container, {
    pixelRatio,
    backgroundColor: background === "white" ? SVG_BACKGROUND_WHITE : undefined,
    width: dimensions.width,
    height: dimensions.height,
    style: {
      // Ensure the element is visible for capture
      transform: "none",
      left: "0",
      top: "0",
    },
  });

  // Race the capture against a timeout to prevent an indefinite hang when
  // the tab is backgrounded and requestAnimationFrame is throttled/paused.
  const canvas = await raceWithTimeout(
    capturePromise,
    CANVAS_CAPTURE_TIMEOUT_MS,
    CANVAS_CAPTURE_TIMEOUT_MESSAGE
  );

  // Guard against a degenerate canvas — html-to-image returns a 0×0 canvas
  // when the container has no painted content. This happens when ExportRenderer
  // threw during React's asynchronous commit phase (no error boundary) and the
  // component tree was silently unmounted before capture began.
  if (canvas.width === 0 || canvas.height === 0) {
    throw new Error(
      "Export failed: captured canvas has zero dimensions. " +
        "The chart may not have rendered correctly."
    );
  }

  return canvas;
}

/**
 * Capture the chart to a canvas using offscreen rendering.
 * Renders the complete chart (including non-visible areas) at the specified
 * zoom level, then converts the DOM to a canvas via html-to-image.
 *
 * The React root is unmounted only after capture (or on error) to ensure the
 * DOM tree is intact during the html-to-image capture phase; tearing it down
 * mid-capture produces a blank PNG.
 *
 * @param params - Layout inputs including tasks, options, column widths, and zoom.
 *   `params.columnWidths` is optional and defaults to `{}` when absent or undefined.
 * @returns A canvas element containing the rendered chart at the target resolution
 * @throws If canvas capture times out (e.g. tab backgrounded during export)
 */
export async function captureChart(
  params: CaptureChartParams
): Promise<HTMLCanvasElement> {
  const { tasks, options, currentAppZoom, projectDateRange, visibleDateRange } =
    params;
  // Normalise once so both calculateExportDimensions and renderAndSettle
  // receive the same concrete value — avoids dual-path `?? {}` guards.
  const columnWidths = params.columnWidths ?? {};

  const dimensions = calculateExportDimensions({ ...params, columnWidths });

  // root is undefined until renderAndSettle returns; the optional-chain guard
  // (root?.unmount()) in the finally block handles the case where
  // renderAndSettle throws before returning.
  let root: Root | undefined;
  // container is undefined until createOffscreenContainer succeeds; the
  // optional-chain guard (container?.remove() via removeOffscreenContainer) in
  // the finally block handles the rare case where createOffscreenContainer
  // itself throws (e.g. SecurityError in a sandboxed iframe) so that a
  // partially-created element does not leak in the DOM.
  let container: HTMLDivElement | undefined;

  try {
    // Create container inside the try block so the finally cleanup always runs
    // even if document.body.appendChild throws (e.g. SecurityError in sandboxed
    // iframes). The container must be on-screen for html-to-image (uses SVG
    // foreignObject); we hide it via opacity:0 and pointer-events:none.
    container = createOffscreenContainer(
      dimensions.width,
      dimensions.height,
      options.background
    );

    // renderAndSettle creates the React root synchronously then waits for the
    // initial paint. Unmounting before toCanvas would tear down the DOM tree
    // mid-capture and produce a blank PNG, so unmounting is deferred to finally.
    root = await renderAndSettle(container, {
      tasks,
      options,
      columnWidths,
      currentAppZoom,
      projectDateRange,
      visibleDateRange,
    });

    // Wait for fonts and paint before triggering capture
    await waitForFonts();
    await waitForPaint();

    return await captureContainerToCanvas(
      container,
      dimensions,
      options.background
    );
  } finally {
    // Unmount the React tree only after capture (or on error), then remove
    // the container from the DOM. root/container may be undefined if an
    // earlier step threw before completing, so the optional-chain guards are
    // intentional.
    root?.unmount();
    if (container) removeOffscreenContainer(container);
  }
}

/**
 * Convert canvas to PNG blob.
 *
 * Note: PNG is lossless — the browser ignores the `quality` argument for
 * "image/png". The parameter is kept for API symmetry in case this function
 * is ever extended to support lossy formats (JPEG/WebP).
 *
 * @param canvas - The canvas element to serialise
 * @param quality - Quality hint (0–1). Ignored for PNG; only used by JPEG/WebP.
 *   The value is clamped to [0, 1] for forward-compatibility with lossy formats.
 * @returns A Blob containing the PNG-encoded image
 */
export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  quality = 1.0
): Promise<Blob> {
  // Clamp to [0, 1] for forward-compatibility with lossy formats (JPEG/WebP).
  // Out-of-range values are silently clamped rather than thrown so callers
  // receive a valid result even if they pass a stale or miscalculated value.
  const clampedQuality = Math.max(0, Math.min(1, quality));
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to convert canvas to blob"));
          }
        },
        "image/png",
        clampedQuality
      );
    } catch (err) {
      // canvas.toBlob can throw a SecurityError synchronously when the canvas
      // has been tainted by cross-origin image data.
      reject(err);
    }
  });
}
